import { Component, EventEmitter, NgZone, Output } from '@angular/core';
import { map, startWith } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import {
  AbcAutoCompleteInputComponent,
  AutoCompletePanelBehaviour,
} from '../abcAutoCompleteInput/abcAutoCompleteInput.component';
import { DomSanitizer } from '@angular/platform-browser';

interface CoordinateOption {
  type: 'normal' | 'decimal' | 'dms' | 'ddm' | 'projected';
  label: string;
  value: Coordinate | string;
}

export interface Coordinate {
  latOrX: number;
  lonOrY: number;
  isProjected: boolean;
}

@Component({
  selector: 'abcAutoCompleteWithCoords',
  styles: [
    '.autoCompleteOption { height: 42px; line-height: 42px; }',
    '.coordinate-option { display: flex; align-items: center; gap: 4px; }',
    `
      ::ng-deep .mat-mdc-autocomplete-panel {
        padding: 0 !important;
        overflow: hidden !important;
        max-height: 252px !important; /* ← das war das Problem */
      }
    `,
    `
      cdk-virtual-scroll-viewport {
        height: 252px; /* 6 × 42px */
        width: 100%;
      }
    `,
    `
      cdk-virtual-scroll-viewport .cdk-virtual-scroll-content-wrapper {
        width: 100%;
      }
    `,
  ],
  template: `
    <mat-form-field [class]="class" floatLabel="never" style="width: 100%">
      <input
        #searchInput
        type="text"
        matInput
        placeholder="{{ placeHolder }}"
        [(ngModel)]="value"
        (mousedown)="searchInputMouseDown($event)"
        (click)="searchInputClick($event)"
        [formControl]="searchInputFC"
        (keydown)="searchInputKeyDown($event, auto)"
        [matAutocomplete]="auto"
        spellcheck="false"
      />
      @if (searchInput.value) {
        <mat-icon (click)="searchInputClear($event, searchInput)" svgIcon="clear" class="iconClear"></mat-icon>
      }
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="optionSelected($event)"
        class="{{ id }}"
        [displayWith]="displayValue"
      >
        <cdk-virtual-scroll-viewport itemSize="42" style="height: 250px">
          <mat-option
            *cdkVirtualFor="let option of filteredOptions | async"
            [value]="option"
            class="autoCompleteOption"
          >
            @switch (option.type) {
              @case ('normal') {
                <span>
                  <p [innerHTML]="option.label | highlight: searchInput.value"></p>
                </span>
              }
              @default {
                <span class="coordinate-option">
                  <mat-icon color="primary">place</mat-icon>
                  {{ option.label }}
                </span>
              }
            }
          </mat-option>
        </cdk-virtual-scroll-viewport>
      </mat-autocomplete>
    </mat-form-field>
  `,
  standalone: false,
})
export class AbcAutoCompleteWithCoordsComponent extends AbcAutoCompleteInputComponent {
  @Output() coordinateSelected = new EventEmitter<CoordinateOption>();
  @Output() clearCoordinateMarkers = new EventEmitter();
  filteredOptions: Observable<CoordinateOption[]>;

  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer, ngZone: NgZone) {
    super(iconRegistry, sanitizer, ngZone);
  }

  override ngOnInit() {
    let originalFilterValue = '';
    let lastWasArrow = false;

    this.searchInputKeyDown = (event: KeyboardEvent, autoComplete) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        lastWasArrow = true;
      } else {
        lastWasArrow = false;
      }
      super.searchInputKeyDown(event, autoComplete);
    };

    this.filteredOptions = this.searchInputFC.valueChanges.pipe(
      startWith(''),
      map((val) => {
        const input = typeof val === 'string' ? val : val?.label || '';
        const search = (lastWasArrow ? originalFilterValue : input)?.toLowerCase()?.trim();

        if (!lastWasArrow) {
          originalFilterValue = input;
        }
        const results: CoordinateOption[] = [];
        if (
          search &&
          search.length >= this.panelDigitsToOpen &&
          this.panelBehaviour === AutoCompletePanelBehaviour.OpenWhenTyping &&
          !this.autocompleteTrigger.panelOpen
        ) {
          this.autocompleteTrigger.openPanel();
        }
        if (
          this.items &&
          (this.panelBehaviour === AutoCompletePanelBehaviour.OpenOnClick || search.length >= this.panelDigitsToOpen)
        ) {
          const decimalCoordRegex = /^\s*(-?\d+(?:\.\d+)?)\s*[, ]+\s*(-?\d+(?:\.\d+)?)\s*$/;
          const dmsCoordRegex =
            /^\s*([+-]?\d{1,3})[°\s]\s*(\d{1,2})['\s]\s*(\d{1,2}(?:\.\d+)?)(?:["”]|''|)?\s*([NnSs])?[,;\s]+\s*([+-]?\d{1,3})[°\s]\s*(\d{1,2})['\s]\s*(\d{1,2}(?:\.\d+)?)(?:["”]|''|)?\s*([EeWw])?\s*$/;
          const ddmRegex = /^\s*([+-]?\d{1,3})[°\s]\s*([\d.]+)\s*[,;\s]+\s*([+-]?\d{1,3})[°\s]\s*([\d.]+)\s*$/;
          const projectedCoordRegex = /^\s*(-?\d+(?:\.\d+)?)\s*[, ]+\s*(-?\d+(?:\.\d+)?)\s*$/;

          const decimalMatch = decimalCoordRegex.exec(input);
          const dmsMatch = dmsCoordRegex.exec(input);
          const ddmMatch = ddmRegex.exec(input);
          const projMatch = projectedCoordRegex.exec(input);

          if (decimalMatch) {
            const lat = parseFloat(decimalMatch[1]);
            const lon = parseFloat(decimalMatch[2]);
            if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
              results.push({
                type: 'decimal',
                label: `${lat}, ${lon}`,
                value: { latOrX: lat, lonOrY: lon, isProjected: false },
              });
            } else {
              results.push({
                type: 'projected',
                label: `${lat}, ${lon}`,
                value: { latOrX: lat, lonOrY: lon, isProjected: true },
              });
            }
          } else if (dmsMatch) {
            const lat = this.dmsToDecimal(dmsMatch[1], dmsMatch[2], dmsMatch[3], dmsMatch[4]);
            const lon = this.dmsToDecimal(dmsMatch[5], dmsMatch[6], dmsMatch[7], dmsMatch[8]);
            results.push({
              type: 'dms',
              label: `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
              value: { latOrX: lat, lonOrY: lon, isProjected: false },
            });
          } else if (ddmMatch) {
            const latDeg = parseFloat(ddmMatch[1]);
            const latMin = parseFloat(ddmMatch[2]);
            const lonDeg = parseFloat(ddmMatch[3]);
            const lonMin = parseFloat(ddmMatch[4]);

            let lat = latDeg + latMin / 60;
            let lon = lonDeg + lonMin / 60;

            if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
              results.push({
                type: 'ddm',
                label: `${latDeg}° ${latMin}', ${lonDeg}° ${lonMin}'`,
                value: { latOrX: lat, lonOrY: lon, isProjected: false },
              });
            }
          } else if (projMatch) {
            const x = parseFloat(projMatch[1]);
            const y = parseFloat(projMatch[2]);
            results.push({
              type: 'projected',
              label: `${x}, ${y}`,
              value: { latOrX: x, lonOrY: y, isProjected: true },
            });
          }

          if (this.items && search) {
            for (const option of this.items) {
              if (option?.toLowerCase()?.includes(search)) {
                results.push({ type: 'normal', label: option, value: option });
              }
            }
          }
        }
        return results;
      }),
    );
  }

  private dmsToDecimal(deg: string, min: string, sec: string | undefined, hemi: string): number {
    const d = parseInt(deg, 10) || 0;
    const m = parseInt(min, 10) || 0;
    const s = parseFloat(sec || '0') || 0;
    let result = d + m / 60 + s / 3600;
    if (/[SsWw]/.test(hemi)) result *= -1;
    return result;
  }

  override optionSelected(event?: any): void {
    if (!event) {
      super.optionSelected();
      return;
    } else {
      const option: CoordinateOption = event.option.value;
      if (option.type !== 'normal') {
        this.coordinateSelected.emit(option);
      } else if (typeof option.value === 'string') {
        this.submit.emit(option?.value);
      }
    }
  }

  override searchInputClear(event, searchInputRef) {
    super.searchInputClear(event, searchInputRef);
    this.clearCoordinateMarkers.emit();
  }

  displayValue(option: CoordinateOption): string {
    if (!option) return '';
    return option.type !== 'normal' ? `Koordinate: ${option.label}` : option.label;
  }

  protected override extractSubmitValue(rawValue: any): string | null {
    const option: CoordinateOption = rawValue;
    if (!option) return null;
    if (option.type !== 'normal') {
      this.coordinateSelected.emit(option);
      return null;
    }
    return typeof option.value === 'string' ? option.value : null;
  }
}
